import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import StatusPill from "@/components/common/StatusPill";
import { toast } from "sonner";
import { messaggioErrore } from "@/lib/preventivi";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useConferma } from "@/hooks/use-conferma";

// Stessi operatori di backend/libreria_punti.py `_predicate_ok` — se qui si
// aggiunge un op, va aggiunto anche lì, altrimenti la condizione fallisce
// sempre lato server.
const OPERATORI = [
  { v: "eq", l: "= uguale a" },
  { v: "ne", l: "≠ diverso da" },
  { v: "gt", l: "> maggiore di" },
  { v: "gte", l: "≥ almeno" },
  { v: "lt", l: "< minore di" },
  { v: "lte", l: "≤ al massimo" },
  { v: "in", l: "tra (elenco separato da virgole)" },
  { v: "not_in", l: "fuori da (elenco separato da virgole)" },
  { v: "exists", l: "presente nella distinta" },
  { v: "contains", l: "che contiene" },
];
const OP_LABEL = Object.fromEntries(OPERATORI.map((o) => [o.v, o.l.split(" ")[0]]));

function riepilogoCondizione(cond) {
  if (!cond || !cond.length) return "Nessun vincolo — sempre proponibile";
  return cond
    .map((p) => {
      if (p.op === "exists") return `${p.path} presente`;
      const val = Array.isArray(p.value) ? p.value.join(", ") : p.value;
      return `${p.path} ${OP_LABEL[p.op] || p.op} ${val}`;
    })
    .join(" · ");
}

function parseValorePredicato(op, raw) {
  const parseUno = (s) => {
    const t = String(s).trim();
    if (t === "") return "";
    if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
    if (t === "true") return true;
    if (t === "false") return false;
    return t;
  };
  if (op === "exists") return null;
  if (op === "in" || op === "not_in") {
    return String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map(parseUno);
  }
  return parseUno(raw);
}

function valoreLeggibile(v) {
  if (v === null || v === undefined) return "";
  return Array.isArray(v) ? v.join(", ") : String(v);
}

const FORM_VUOTO = {
  codice: "",
  titolo: "",
  marca: "",
  categoria: "",
  cosa_si_vede: "",
  cosa_c_e_dietro: "",
  perche_conta: "",
  domanda_da_fare: "",
  attivo: true,
  condizione: [], // [{ path, op, raw }]
};

export default function PuntiDistintiviTab() {
  const { chiedi, dialogoConferma } = useConferma();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(FORM_VUOTO);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/libreria-punti");
      setRows(r.data || []);
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const apriNuova = () => {
    setEditingId(null);
    setForm(FORM_VUOTO);
    setOpen(true);
  };

  const apriModifica = (voce) => {
    setEditingId(voce.id);
    setForm({
      codice: voce.codice || "",
      titolo: voce.titolo || "",
      marca: voce.marca || "",
      categoria: voce.categoria || "",
      cosa_si_vede: voce.cosa_si_vede || "",
      cosa_c_e_dietro: voce.cosa_c_e_dietro || "",
      perche_conta: voce.perche_conta || "",
      domanda_da_fare: voce.domanda_da_fare || "",
      attivo: voce.attivo,
      condizione: (voce.condizione || []).map((p) => ({
        path: p.path || "",
        op: p.op || "eq",
        raw: valoreLeggibile(p.value),
      })),
    });
    setOpen(true);
  };

  const aggiungiRigaCondizione = () => {
    setForm((f) => ({ ...f, condizione: [...f.condizione, { path: "", op: "eq", raw: "" }] }));
  };
  const patchRigaCondizione = (i, patch) => {
    setForm((f) => ({
      ...f,
      condizione: f.condizione.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    }));
  };
  const rimuoviRigaCondizione = (i) => {
    setForm((f) => ({ ...f, condizione: f.condizione.filter((_, idx) => idx !== i) }));
  };

  const submit = async () => {
    if (!form.codice.trim() || !form.titolo.trim() || !form.cosa_si_vede.trim()) {
      toast.error("Codice, titolo e «cosa si vede» sono obbligatori.");
      return;
    }
    if (!editingId && rows.some((r) => r.codice === form.codice.trim())) {
      toast.error(`Il codice «${form.codice.trim()}» esiste già: scegline un altro.`);
      return;
    }
    const condVuota = form.condizione.find((r) => !r.path.trim());
    if (condVuota) {
      toast.error("Ogni riga di condizione vuole un percorso — es. top.spessore_cm.");
      return;
    }
    const condizione = form.condizione.map((r) => ({
      path: r.path.trim(),
      op: r.op,
      value: parseValorePredicato(r.op, r.raw),
    }));

    setBusy(true);
    try {
      const payloadBase = {
        titolo: form.titolo.trim(),
        marca: form.marca.trim() || null,
        categoria: form.categoria.trim() || null,
        cosa_si_vede: form.cosa_si_vede.trim(),
        cosa_c_e_dietro: form.cosa_c_e_dietro.trim() || null,
        perche_conta: form.perche_conta.trim() || null,
        domanda_da_fare: form.domanda_da_fare.trim() || null,
        condizione,
      };
      if (editingId) {
        await api.patch(`/libreria-punti/${editingId}`, { ...payloadBase, attivo: form.attivo });
        toast.success("Voce aggiornata");
      } else {
        await api.post("/libreria-punti", { ...payloadBase, codice: form.codice.trim(), attivo: true });
        toast.success("Voce creata");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleAttivo = async (voce, v) => {
    try {
      await api.patch(`/libreria-punti/${voce.id}`, { attivo: v });
      await load();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const elimina = async (voce) => {
    const ok = await chiedi({
      titolo: "Eliminare questa voce dalla libreria?",
      descrizione:
        "Sparisce dalle proponibili sui nuovi preventivi. I punti già scelti su preventivi esistenti restano — sono una copia, non un riferimento vivo.",
      verbo: "Elimina la voce",
    });
    if (!ok) return;
    try {
      await api.delete(`/libreria-punti/${voce.id}`);
      await load();
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };

  const attive = rows.filter((r) => r.attivo);

  return (
    <div className="space-y-6" data-testid="punti-distintivi-tab">
      {dialogoConferma}
      <Card className="p-card space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-foreground">Libreria punti distintivi</p>
            <p className="text-xs text-muted-foreground mt-1">
              Le voci che il venditore può scegliere sul blocco «Punti distintivi» di un preventivo — da 3 a 5
              per documento. Una voce con condizione entra in proposta solo se la distinta del preventivo la
              soddisfa.
            </p>
          </div>
          <Button onClick={apriNuova} data-testid="btn-nuova-voce-punti">
            <Plus className="h-4 w-4 mr-1.5" /> Nuova voce
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="bg-muted px-4 py-2.5 border-b">
          <p className="text-eyebrow uppercase text-muted-foreground">
            {rows.length} voci totali · {attive.length} attive
          </p>
        </div>
        {rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">
            La libreria è vuota. Crea la prima voce: senza libreria nessun preventivo può avere punti
            distintivi, e nessun preventivo si può pubblicare.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((voce) => (
              <li
                key={voce.id}
                className={`px-4 py-3 space-y-1.5 ${voce.attivo ? "" : "opacity-60"}`}
                data-testid={`punti-tab-riga-${voce.codice}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground">{voce.titolo}</span>
                      <StatusPill stato="neutro" dot={false} className="uppercase">
                        {voce.codice}
                      </StatusPill>
                      {voce.categoria ? (
                        <StatusPill stato="info" dot={false}>
                          {voce.categoria}
                        </StatusPill>
                      ) : null}
                      {voce.marca ? (
                        <StatusPill stato="neutro" dot={false}>
                          {voce.marca}
                        </StatusPill>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{voce.cosa_si_vede}</p>
                    <p className="text-xs text-muted-foreground mt-1 italic">
                      {riepilogoCondizione(voce.condizione)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch
                      checked={voce.attivo}
                      onCheckedChange={(v) => toggleAttivo(voce, v)}
                      data-testid={`punti-tab-attivo-${voce.codice}`}
                      className="relative after:absolute after:inset-x-0 after:-inset-y-2.5 after:content-['']"
                    />
                    <button
                      type="button"
                      onClick={() => apriModifica(voce)}
                      aria-label="Modifica la voce"
                      className="inline-flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-2 hover:text-foreground"
                      data-testid={`punti-tab-modifica-${voce.codice}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => elimina(voce)}
                      aria-label="Togli la voce"
                      className="inline-flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-surface-2 hover:text-danger-foreground"
                      data-testid={`punti-tab-elimina-${voce.codice}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-voce-punti">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica voce" : "Nuova voce di libreria"}</DialogTitle>
            <DialogDescription>
              Scritta in 4 tempi: cosa si vede, cosa c&#39;è dietro, perché conta, che domanda fare al cliente.
              La condizione (facoltativa) decide su quali preventivi la voce è proponibile.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-inside">
                <Label className="text-xs text-muted-foreground">Codice</Label>
                <Input
                  value={form.codice}
                  disabled={!!editingId}
                  onChange={(e) => setForm({ ...form, codice: e.target.value })}
                  placeholder="es. IDA-06"
                  data-testid="voce-codice"
                />
              </div>
              <div className="space-y-inside">
                <Label className="text-xs text-muted-foreground">Marca (facoltativa)</Label>
                <Input
                  value={form.marca}
                  onChange={(e) => setForm({ ...form, marca: e.target.value })}
                  data-testid="voce-marca"
                />
              </div>
              <div className="space-y-inside">
                <Label className="text-xs text-muted-foreground">Categoria (facoltativa)</Label>
                <Input
                  value={form.categoria}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  placeholder="es. materiali, processo, azienda"
                  data-testid="voce-categoria"
                />
              </div>
            </div>

            <div className="space-y-inside">
              <Label className="text-xs text-muted-foreground">Titolo</Label>
              <Input
                value={form.titolo}
                onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                data-testid="voce-titolo"
              />
            </div>
            <div className="space-y-inside">
              <Label className="text-xs text-muted-foreground">Cosa si vede</Label>
              <Textarea
                rows={2}
                value={form.cosa_si_vede}
                onChange={(e) => setForm({ ...form, cosa_si_vede: e.target.value })}
                data-testid="voce-cosa-si-vede"
              />
            </div>
            <div className="space-y-inside">
              <Label className="text-xs text-muted-foreground">Cosa c&#39;è dietro (facoltativo)</Label>
              <Textarea
                rows={2}
                value={form.cosa_c_e_dietro}
                onChange={(e) => setForm({ ...form, cosa_c_e_dietro: e.target.value })}
                data-testid="voce-cosa-c-e-dietro"
              />
            </div>
            <div className="space-y-inside">
              <Label className="text-xs text-muted-foreground">Perché conta (facoltativo)</Label>
              <Textarea
                rows={2}
                value={form.perche_conta}
                onChange={(e) => setForm({ ...form, perche_conta: e.target.value })}
                data-testid="voce-perche-conta"
              />
            </div>
            <div className="space-y-inside">
              <Label className="text-xs text-muted-foreground">Domanda da fare (facoltativa)</Label>
              <Textarea
                rows={2}
                value={form.domanda_da_fare}
                onChange={(e) => setForm({ ...form, domanda_da_fare: e.target.value })}
                data-testid="voce-domanda-da-fare"
              />
            </div>

            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">
                  Condizione sulla distinta (vuota = sempre proponibile)
                </Label>
                <Button size="sm" variant="outline" onClick={aggiungiRigaCondizione} data-testid="voce-cond-add">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi riga
                </Button>
              </div>
              {form.condizione.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Nessun vincolo: la voce è sempre proponibile.</p>
              ) : (
                <div className="space-y-2">
                  {form.condizione.map((riga, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`voce-cond-riga-${i}`}>
                      <Input
                        className="col-span-5"
                        value={riga.path}
                        onChange={(e) => patchRigaCondizione(i, { path: e.target.value })}
                        placeholder="es. top.spessore_cm"
                        data-testid={`voce-cond-path-${i}`}
                      />
                      <Select value={riga.op} onValueChange={(v) => patchRigaCondizione(i, { op: v })}>
                        <SelectTrigger className="col-span-4" data-testid={`voce-cond-op-${i}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OPERATORI.map((o) => (
                            <SelectItem key={o.v} value={o.v}>
                              {o.l}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        className="col-span-2"
                        value={riga.raw}
                        disabled={riga.op === "exists"}
                        onChange={(e) => patchRigaCondizione(i, { raw: e.target.value })}
                        placeholder={riga.op === "in" || riga.op === "not_in" ? "a, b, c" : "valore"}
                        data-testid={`voce-cond-value-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => rimuoviRigaCondizione(i)}
                        className="col-span-1 text-muted-foreground hover:text-danger-foreground"
                        data-testid={`voce-cond-del-${i}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {editingId ? (
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.attivo}
                  onCheckedChange={(v) => setForm({ ...form, attivo: v })}
                  data-testid="voce-attivo"
                />
                <Label className="text-xs text-muted-foreground">Attiva (proponibile ai venditori)</Label>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="voce-annulla">
              Annulla
            </Button>
            <Button onClick={submit} disabled={busy} data-testid="voce-salva">
              {busy ? "Salvataggio…" : editingId ? "Salva modifiche" : "Crea voce"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
